import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  CardHeader,
  Typography,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { CreateSystemRequest, UpdateSystemRequest, SystemModuleResponse } from '../../../app/services/api/generated';

const SystemForm = () => {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const { getSystemById, createSystem, updateSystem } = useNswagClient();
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [modules, setModules] = useState<SystemModuleResponse[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchSystem = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const response = await getSystemById(parseInt(id));
      if (response?.data) {
        const sys = response.data;
        setName(sys.name ?? '');
        setDescription(sys.description ?? '');
        setModules(sys.modules ?? []);
      }
    } catch (error) {
      console.error('Error fetching system:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchSystem();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name?.trim()) {
      newErrors.name = t('systems.nameRequired');
    } else if (name.trim().length > 50) {
      newErrors.name = t('systems.nameTooLong');
    }

    if (description && description.length > 255) {
      newErrors.description = t('systems.descriptionTooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const body = {
          name: name.trim(),
          description: description.trim(),
        } as UpdateSystemRequest;
        await updateSystem(parseInt(id!), body);
        setMessage(t('systems.updateSuccess'));
        setIsSuccess(true);
        setShowMessage(true);
        setTimeout(() => {
          navigate('/admin/systems');
        }, 500);
      } else {
        const body = {
          name: name.trim(),
          description: description.trim(),
        } as CreateSystemRequest;
        await createSystem(body);
        setMessage(t('systems.createSuccess'));
        setIsSuccess(true);
        setShowMessage(true);
        setTimeout(() => {
          navigate('/admin/systems');
        }, 500);
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('systems.updateError') : t('systems.createError')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/systems');
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'SystemUpdate' : 'SystemCreate']);
  const canView = hasPermissionTo(['SystemView']);

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('systems.accessDenied')}</PageTitle>
        <Typography>{t('systems.insufficientPermissions')}</Typography>
      </PageArea>
    );
  }

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('systems.editSystem') : t('systems.createNew')}</PageTitle>
        <Typography>{t('systems.loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        {/* Basic System Information */}
        <Card elevation={1}>
          <CardHeader
            title={isEdit ? t('systems.editSystem') : t('systems.createNew')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={8}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('systems.name')}
                  placeholder={t('systems.namePlaceholder')}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                  }}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  inputProps={{ maxLength: 50 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('systems.description')}
                  placeholder={t('systems.descriptionPlaceholder')}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description) setErrors(prev => ({ ...prev, description: '' }));
                  }}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 255 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Associated Modules (read-only, shown only in edit mode) */}
        {isEdit && modules.length > 0 && (
          <Card elevation={1}>
            <CardHeader
              title={t('systems.associatedModules')}
              subheader={t('systems.associatedModulesSubtitle')}
              sx={{ color: 'text.primary' }}
            />
            <CardContent sx={{ p: 8 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('systems.moduleName')}</TableCell>
                      <TableCell>{t('systems.moduleDescription')}</TableCell>
                      <TableCell align="center">{t('systems.modulePremium')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modules.map((mod) => (
                      <TableRow key={mod.id}>
                        <TableCell>{mod.name}</TableCell>
                        <TableCell>{mod.description || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={mod.premium ? t('systems.yes') : t('systems.no')}
                            color={mod.premium ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                {t('systems.cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{ minWidth: 120, borderRadius: 2 }}
                >
                  {isSaving ? t('systems.saving') : isEdit ? t('systems.update') : t('systems.create')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default SystemForm;
